import { useState, useEffect } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import translations from "@/constants/translations";
import { HOME } from "@/constants/testIds";
import { ChevronDown, Phone, MapPin, UtensilsCrossed, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}/api/files/${path}`;
};

export default function Home() {
  const [settings, setSettings] = useState({});
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const t = translations[lang];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/settings`),
      axios.get(`${API}/categories`),
      axios.get(`${API}/dishes`),
      axios.get(`${API}/gallery`),
    ]).then(([s, c, d, g]) => {
      setSettings(s.data || {});
      setCategories(Array.isArray(c.data) ? c.data : []);
      setDishes(Array.isArray(d.data) ? d.data : []);
      setGallery(Array.isArray(g.data) ? g.data : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const featuredDishes = dishes.filter(d => d.featured && d.available).slice(0, 3);
  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return "";
    return lang === "pt" ? cat.name_pt : cat.name_en;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0077B6] border-t-transparent" />
      </div>
    );
  }

  const heroImage = getImageUrl(settings.hero_image) || "https://images.unsplash.com/photo-1652195960911-c9f55224bd89?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

  return (
    <div className="min-h-screen font-lato">
      <Header settings={settings} />

      {/* ========== HERO ========== */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="O Quinto Império"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65" />
        </div>

        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          {/* Logo */}
          {settings.logo_footer && (
            <div className="mb-8 animate-fade-in flex justify-center">
              <div className="bg-white/95 rounded-full p-4 shadow-2xl backdrop-blur-sm">
                <img
                  src={getImageUrl(settings.logo_footer)}
                  alt="O Quinto Império"
                  className="h-20 md:h-24 w-auto object-contain"
                />
              </div>
            </div>
          )}

          {/* Title */}
          <h1
            data-testid={HOME.heroTitle}
            className="font-cormorant text-5xl sm:text-6xl md:text-7xl font-light leading-tight mb-4 drop-shadow-lg"
          >
            {lang === "pt" ? settings.hero_title_pt : settings.hero_title_en}
          </h1>

          {/* Divider */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#0077B6]" />
            <span className="font-lato text-xs tracking-[0.3em] uppercase text-[#0077B6]">
              — RESTAURANTE —
            </span>
            <div className="h-px w-12 bg-[#0077B6]" />
          </div>

          {/* Subtitle */}
          <p
            data-testid={HOME.heroSubtitle}
            className="font-lato text-base md:text-lg tracking-widest uppercase opacity-85 mb-10"
          >
            {lang === "pt" ? settings.hero_subtitle_pt : settings.hero_subtitle_en}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={settings.reservation_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={HOME.heroReserveBtn}
              className="px-10 py-4 bg-[#0077B6] text-white font-lato text-sm tracking-[0.2em] uppercase hover:bg-[#005f8f] transition-colors duration-200 shadow-lg"
            >
              {t.hero.cta}
            </a>
            <a
              href="/menu"
              data-testid={HOME.heroMenuBtn}
              className="px-10 py-4 border border-white text-white font-lato text-sm tracking-[0.2em] uppercase hover:bg-white/15 transition-colors duration-200"
            >
              {t.hero.menuBtn}
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 animate-bounce">
          <ChevronDown size={26} />
        </div>
      </section>

      {/* ========== ABOUT ========== */}
      <section data-testid={HOME.aboutSection} className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Text */}
            <div>
              <span className="font-dancing text-[#0077B6] text-xl block mb-3">
                {t.about.badge}
              </span>
              <h2 className="font-cormorant text-4xl md:text-5xl font-light text-[#4A4A4A] mb-2 leading-tight">
                {lang === "pt" ? settings.about_title_pt : settings.about_title_en}
              </h2>
              <div className="w-16 h-0.5 bg-[#C00000] mb-6" />
              <p className="font-lato text-gray-600 leading-relaxed text-base">
                {lang === "pt" ? settings.about_text_pt : settings.about_text_en}
              </p>

              <div className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-gray-100">
                <div className="text-center">
                  <p className="font-cormorant text-3xl text-[#0077B6] font-semibold">25€</p>
                  <p className="font-lato text-xs text-gray-500 tracking-widest uppercase mt-1">{t.about.avgPrice}</p>
                </div>
                <div className="text-center border-x border-gray-100">
                  <p className="font-cormorant text-lg text-[#4A4A4A] font-medium leading-tight">
                    {t.about.cuisine}
                  </p>
                  <p className="font-lato text-xs text-gray-500 tracking-widest uppercase mt-1">Cozinha</p>
                </div>
                <div className="text-center">
                  <p className="font-cormorant text-lg text-[#4A4A4A] font-medium leading-tight">
                    {t.about.location}
                  </p>
                  <p className="font-lato text-xs text-gray-500 tracking-widest uppercase mt-1">Local</p>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="overflow-hidden shadow-2xl">
                {settings.about_image ? (
                  <img
                    src={getImageUrl(settings.about_image)}
                    alt="O Quinto Império"
                    className="w-full h-72 md:h-96 object-cover hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-72 md:h-96 bg-gray-100 flex items-center justify-center text-gray-400">
                    <UtensilsCrossed size={40} />
                  </div>
                )}
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-[#0077B6]/30 -z-10 hidden md:block" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== MENU PREVIEW ========== */}
      <section data-testid={HOME.menuSection} className="py-20 md:py-28 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-dancing text-[#0077B6] text-xl">{t.menu.badge}</span>
            <h2 className="font-cormorant text-4xl md:text-5xl font-light text-[#4A4A4A] mt-1 mb-2">
              {t.menu.title}
            </h2>
            <div className="w-12 h-0.5 bg-[#C00000] mx-auto" />
            <p className="font-lato text-gray-500 mt-4 text-sm tracking-wider">
              {t.menu.subtitle}
            </p>
          </div>

          {featuredDishes.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {featuredDishes.map((dish) => (
                <div
                  key={dish.id}
                  className="bg-white shadow-sm hover:shadow-xl transition-shadow duration-300 group"
                >
                  {/* Dish image */}
                  <div className="overflow-hidden h-52">
                    {dish.image_path ? (
                      <img
                        src={getImageUrl(dish.image_path)}
                        alt={lang === "pt" ? dish.name_pt : dish.name_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-50 flex flex-col items-center justify-center gap-2 text-gray-300">
                        <UtensilsCrossed size={36} />
                        <span className="font-lato text-xs">{t.menu.noImage}</span>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <p className="font-lato text-xs text-[#0077B6] tracking-[0.2em] uppercase mb-1.5">
                      {getCategoryName(dish.category_id)}
                    </p>
                    <h3 className="font-cormorant text-2xl text-[#4A4A4A] mb-2 font-medium">
                      {lang === "pt" ? dish.name_pt : dish.name_en}
                    </h3>
                    <p className="font-lato text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">
                      {lang === "pt" ? dish.description_pt : dish.description_en}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="font-cormorant text-2xl text-[#C00000] font-semibold">
                        {dish.price.toFixed(2)}€
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <UtensilsCrossed size={40} className="mx-auto mb-3" />
              <p className="font-lato">Ementa em breve</p>
            </div>
          )}

          <div className="text-center mt-12">
            <a
              href="/menu"
              className="inline-flex items-center gap-2 font-lato text-sm tracking-widest uppercase border border-[#0077B6] text-[#0077B6] px-8 py-3 hover:bg-[#0077B6] hover:text-white transition-colors duration-200"
            >
              {t.menu.viewAll}
              <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ========== GALLERY ========== */}
      {gallery.length > 0 && (
        <section data-testid={HOME.gallerySection} className="py-20 md:py-28 bg-[#111827]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="font-dancing text-[#0077B6] text-xl">{t.gallery.badge}</span>
              <h2 className="font-cormorant text-4xl md:text-5xl font-light text-white mt-1 mb-2">
                {t.gallery.title}
              </h2>
              <div className="w-12 h-0.5 bg-[#C00000] mx-auto" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              {gallery.slice(0, 8).map((img, i) => (
                <div
                  key={img.id}
                  className={`overflow-hidden group cursor-pointer ${
                    i === 0 || i === 5 ? "md:col-span-2 md:row-span-2" : ""
                  }`}
                >
                  <div className={`${i === 0 || i === 5 ? "h-64 md:h-full" : "h-40 md:h-44"} relative`}>
                    <img
                      src={getImageUrl(img.image_path)}
                      alt={lang === "pt" ? img.caption_pt : img.caption_en}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {(img.caption_pt || img.caption_en) && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <p className="font-lato text-white text-xs">
                          {lang === "pt" ? img.caption_pt : img.caption_en}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== LOCATION & CONTACT ========== */}
      <section id="location" data-testid={HOME.locationSection} className="py-0">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2">
            {/* Map */}
            <div className="h-72 md:h-[500px]">
              {settings.maps_embed_url ? (
                <iframe
                  src={settings.maps_embed_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                  <MapPin size={40} />
                </div>
              )}
            </div>

            {/* Info panel */}
            <div className="bg-[#111827] text-white px-8 md:px-14 py-14 flex flex-col justify-center">
              <span className="font-dancing text-[#0077B6] text-xl block mb-3">
                {t.location.badge}
              </span>
              <h2 className="font-cormorant text-4xl md:text-5xl font-light mb-8">
                {t.location.title}
              </h2>

              <div className="space-y-6">
                <div>
                  <h4 className="font-lato text-xs tracking-widest uppercase text-[#0077B6] mb-1.5">
                    {t.location.address}
                  </h4>
                  <p className="font-lato text-sm text-gray-300">{settings.address}</p>
                </div>

                <div>
                  <h4 className="font-lato text-xs tracking-widest uppercase text-[#0077B6] mb-1.5">
                    {t.location.hours}
                  </h4>
                  <p className="font-lato text-sm text-gray-300 whitespace-pre-line">
                    {lang === "pt" ? settings.hours_pt : settings.hours_en}
                  </p>
                </div>

                <div>
                  <h4 className="font-lato text-xs tracking-widest uppercase text-[#0077B6] mb-1.5">
                    {t.location.contact}
                  </h4>
                  {settings.phone && (
                    <a
                      href={`tel:${settings.phone}`}
                      className="font-lato text-sm text-gray-300 hover:text-[#0077B6] transition-colors duration-200 block"
                    >
                      {settings.phone}
                    </a>
                  )}
                  {settings.email && (
                    <a
                      href={`mailto:${settings.email}`}
                      className="font-lato text-sm text-gray-300 hover:text-[#0077B6] transition-colors duration-200 block mt-0.5"
                    >
                      {settings.email}
                    </a>
                  )}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                {settings.phone && (
                  <a
                    href={`tel:${settings.phone}`}
                    data-testid={HOME.callBtn}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-[#0077B6] text-white font-lato text-xs tracking-widest uppercase hover:bg-[#005f8f] transition-colors duration-200"
                  >
                    <Phone size={14} />
                    {t.location.call}
                  </a>
                )}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings.address || "O Quinto Império, Portimão")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={HOME.directionsBtn}
                  className="flex items-center justify-center gap-2 px-6 py-3 border border-white/40 text-white font-lato text-xs tracking-widest uppercase hover:bg-white/10 transition-colors duration-200"
                >
                  <MapPin size={14} />
                  {t.location.directions}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
}
